import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Check,
  ArrowRight,
  MapPin,
  Phone,
  Star,
  Users,
  Zap,
  Shield,
  Clock,
  IndianRupee,
  Building2,
  Smartphone,
  MessageSquare,
} from "lucide-react";

// City-specific data for SEO
const cityData = {
  mumbai: {
    name: "Mumbai",
    state: "Maharashtra",
    population: "20 million",
    repairShops: "2,500+",
    tagline: "Repair Shop Software for Mumbai's Thriving Electronics Market",
    description: "Join 50+ repair shops in Mumbai using AfterSales.pro. From Lamington Road to Gaffar Market, manage your mobile and laptop repair business efficiently.",
    areas: ["Lamington Road", "Gaffar Market", "Dadar", "Andheri", "Borivali", "Thane", "Navi Mumbai", "Mulund"],
    testimonial: {
      name: "Rajesh Sharma",
      shop: "NeoStore Repairs, Mulund",
      text: "AfterSales.pro transformed our repair business. We now handle 3x more repairs with better customer satisfaction.",
      rating: 5
    },
    keywords: "repair shop software mumbai, mobile repair software mumbai, laptop repair software mumbai, service center software mumbai, repair management mumbai",
    localStats: { shops: 50, jobs: "15,000+", satisfaction: "98%" }
  },
  delhi: {
    name: "Delhi",
    state: "Delhi NCR",
    population: "32 million",
    repairShops: "4,000+",
    tagline: "Leading Repair Shop Software in Delhi NCR",
    description: "Trusted by 80+ repair shops across Delhi, Noida, and Gurgaon. AfterSales.pro helps you compete in India's largest electronics market.",
    areas: ["Nehru Place", "Gaffar Market", "Karol Bagh", "Lajpat Nagar", "Noida", "Gurgaon", "Dwarka", "Connaught Place"],
    testimonial: {
      name: "Amit Kumar",
      shop: "Quick Fix Electronics, Nehru Place",
      text: "Managing 3 branches across Delhi NCR was chaos before AfterSales.pro. Now everything is in one place.",
      rating: 5
    },
    keywords: "repair shop software delhi, mobile repair software delhi ncr, laptop repair software noida, service center software gurgaon, repair management delhi",
    localStats: { shops: 80, jobs: "25,000+", satisfaction: "97%" }
  },
  bangalore: {
    name: "Bangalore",
    state: "Karnataka",
    population: "13 million",
    repairShops: "3,000+",
    tagline: "Tech-Forward Repair Shop Software for Bangalore",
    description: "India's tech capital deserves the best repair shop software. Join 60+ Bangalore repair shops using AfterSales.pro for efficient operations.",
    areas: ["SP Road", "Brigade Road", "Koramangala", "Indiranagar", "Whitefield", "Electronic City", "HSR Layout", "Jayanagar"],
    testimonial: {
      name: "Suresh Reddy",
      shop: "TechCare Solutions, SP Road",
      text: "The WhatsApp integration alone saved us 2 hours daily. Customers love the real-time updates.",
      rating: 5
    },
    keywords: "repair shop software bangalore, mobile repair software bengaluru, laptop repair software bangalore, service center software karnataka, repair management bangalore",
    localStats: { shops: 60, jobs: "18,000+", satisfaction: "98%" }
  },
  hyderabad: {
    name: "Hyderabad",
    state: "Telangana",
    population: "10 million",
    repairShops: "2,000+",
    tagline: "Repair Shop Management Software for Hyderabad",
    description: "From Secunderabad to Hi-Tech City, Hyderabad's repair shops trust AfterSales.pro. Join 40+ businesses already using our platform.",
    areas: ["Secunderabad", "Ameerpet", "Hi-Tech City", "Kukatpally", "Dilsukhnagar", "ECIL", "Begumpet", "Madhapur"],
    testimonial: {
      name: "Mohammed Irfan",
      shop: "Mobile Hub, Ameerpet",
      text: "Finally a software that understands Indian repair shops. GST invoicing and WhatsApp updates are game changers.",
      rating: 5
    },
    keywords: "repair shop software hyderabad, mobile repair software telangana, laptop repair software secunderabad, service center software hyderabad, repair management hyderabad",
    localStats: { shops: 40, jobs: "12,000+", satisfaction: "97%" }
  },
  chennai: {
    name: "Chennai",
    state: "Tamil Nadu",
    population: "11 million",
    repairShops: "2,200+",
    tagline: "Repair Shop Software for Chennai's Electronics Hub",
    description: "Chennai's repair shops choose AfterSales.pro for reliable service and local support. Join 35+ businesses managing repairs efficiently.",
    areas: ["Ritchie Street", "T Nagar", "Anna Nagar", "Velachery", "Tambaram", "Porur", "OMR", "Adyar"],
    testimonial: {
      name: "Senthil Kumar",
      shop: "Sri Ganesh Mobiles, Ritchie Street",
      text: "Customer tracking and job management became so easy. Our repeat customers increased by 40%.",
      rating: 5
    },
    keywords: "repair shop software chennai, mobile repair software tamil nadu, laptop repair software chennai, service center software chennai, repair management chennai",
    localStats: { shops: 35, jobs: "10,000+", satisfaction: "98%" }
  },
  pune: {
    name: "Pune",
    state: "Maharashtra",
    population: "7 million",
    repairShops: "1,500+",
    tagline: "Modern Repair Shop Software for Pune",
    description: "Pune's growing repair industry trusts AfterSales.pro. From FC Road to Hinjewadi, manage your repair business like a pro.",
    areas: ["FC Road", "JM Road", "Hinjewadi", "Kothrud", "Viman Nagar", "Wakad", "Pimpri-Chinchwad", "Kharadi"],
    testimonial: {
      name: "Vikram Patil",
      shop: "PhoneDoctor, FC Road",
      text: "Started with the free plan, now on Premium. The software paid for itself in the first month.",
      rating: 5
    },
    keywords: "repair shop software pune, mobile repair software pune, laptop repair software maharashtra, service center software pune, repair management pune",
    localStats: { shops: 30, jobs: "8,000+", satisfaction: "97%" }
  },
  ahmedabad: {
    name: "Ahmedabad",
    state: "Gujarat",
    population: "8 million",
    repairShops: "1,800+",
    tagline: "Repair Shop Management for Ahmedabad Businesses",
    description: "Gujarat's commercial capital chooses AfterSales.pro for reliable repair shop management. Trusted by 25+ Ahmedabad businesses.",
    areas: ["CG Road", "SG Highway", "Satellite", "Navrangpura", "Maninagar", "Bopal", "Gandhinagar", "Vastrapur"],
    testimonial: {
      name: "Ketan Patel",
      shop: "GadgetFix, CG Road",
      text: "Bilingual support in Gujarati helped my team adopt the software quickly. Excellent customer service!",
      rating: 5
    },
    keywords: "repair shop software ahmedabad, mobile repair software gujarat, laptop repair software ahmedabad, service center software gujarat, repair management ahmedabad",
    localStats: { shops: 25, jobs: "7,000+", satisfaction: "98%" }
  },
  kolkata: {
    name: "Kolkata",
    state: "West Bengal",
    population: "15 million",
    repairShops: "2,000+",
    tagline: "Trusted Repair Shop Software in Kolkata",
    description: "From Chandni Chowk to Salt Lake, Kolkata's repair shops manage their business efficiently with AfterSales.pro.",
    areas: ["Chandni Chowk", "Park Street", "Salt Lake", "Howrah", "New Town", "Gariahat", "Esplanade", "Ballygunge"],
    testimonial: {
      name: "Subhash Ghosh",
      shop: "Mobile Care, Chandni Chowk",
      text: "Managing inventory across repairs was a nightmare. AfterSales.pro made it simple and trackable.",
      rating: 5
    },
    keywords: "repair shop software kolkata, mobile repair software west bengal, laptop repair software kolkata, service center software kolkata, repair management kolkata",
    localStats: { shops: 30, jobs: "9,000+", satisfaction: "97%" }
  },
  jaipur: {
    name: "Jaipur",
    state: "Rajasthan",
    population: "4 million",
    repairShops: "1,000+",
    tagline: "Repair Shop Software for Jaipur's Growing Market",
    description: "Pink City's repair shops go digital with AfterSales.pro. Simple, affordable, and built for Indian businesses.",
    areas: ["Nehru Bazaar", "MI Road", "Vaishali Nagar", "Malviya Nagar", "Mansarovar", "Tonk Road", "Raja Park", "Sitapura"],
    testimonial: {
      name: "Rahul Sharma",
      shop: "Digital Seva, MI Road",
      text: "The pricing is perfect for a growing business like ours. No hidden charges, no surprises.",
      rating: 5
    },
    keywords: "repair shop software jaipur, mobile repair software rajasthan, laptop repair software jaipur, service center software jaipur, repair management jaipur",
    localStats: { shops: 20, jobs: "5,000+", satisfaction: "98%" }
  },
  lucknow: {
    name: "Lucknow",
    state: "Uttar Pradesh",
    population: "4 million",
    repairShops: "1,200+",
    tagline: "Best Repair Shop Software in Lucknow",
    description: "Lucknow's repair businesses trust AfterSales.pro for reliable, affordable shop management. Join the digital transformation.",
    areas: ["Hazratganj", "Aminabad", "Alambagh", "Gomti Nagar", "Aliganj", "Indira Nagar", "Rajajipuram", "Mahanagar"],
    testimonial: {
      name: "Ajay Singh",
      shop: "Mobile Clinic, Hazratganj",
      text: "Hindi interface option made training my staff so much easier. Great for local businesses!",
      rating: 5
    },
    keywords: "repair shop software lucknow, mobile repair software uttar pradesh, laptop repair software lucknow, service center software lucknow, repair management lucknow",
    localStats: { shops: 15, jobs: "4,000+", satisfaction: "97%" }
  },
};

const features = [
  { icon: Smartphone, title: "IMEI & Serial Tracking", desc: "Track every device with unique identifiers" },
  { icon: MessageSquare, title: "WhatsApp Updates", desc: "Automated customer notifications" },
  { icon: IndianRupee, title: "GST Invoicing", desc: "Compliant billing for Indian businesses" },
  { icon: Building2, title: "Multi-Branch", desc: "Manage multiple locations easily" },
  { icon: Shield, title: "Data Security", desc: "Your customer data is protected" },
  { icon: Clock, title: "24/7 Support", desc: "Always here when you need help" },
];

export default function CityLandingPage() {
  const navigate = useNavigate();
  const { city } = useParams();
  const data = cityData[city?.toLowerCase()];

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">City Not Found</h1>
          <Button onClick={() => navigate("/")}>Go to Homepage</Button>
        </div>
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `AfterSales.pro - Repair Shop Software ${data.name}`,
    "description": data.description,
    "url": `https://aftersales.pro/city/${city}`,
    "areaServed": {
      "@type": "City",
      "name": data.name,
      "containedInPlace": {
        "@type": "State",
        "name": data.state
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": data.localStats.shops
    }
  };

  return (
    <>
      <Helmet>
        <title>{`Repair Shop Software in ${data.name} | AfterSales.pro - #1 Choice`}</title>
        <meta name="description" content={`${data.description} Trusted by ${data.localStats.shops}+ repair shops in ${data.name}. Start free today!`} />
        <meta name="keywords" content={data.keywords} />
        <link rel="canonical" href={`https://aftersales.pro/city/${city}`} />
        
        <meta property="og:title" content={`Best Repair Shop Software in ${data.name} | AfterSales.pro`} />
        <meta property="og:description" content={data.description} />
        <meta property="og:url" content={`https://aftersales.pro/city/${city}`} />
        <meta property="og:type" content="website" />
        
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content={data.name} />
        
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
                <img src="/logo.svg" alt="AfterSales.pro" className="h-8" />
              </div>
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => navigate("/login")}>Sign In</Button>
                <Button onClick={() => navigate("/signup")}>Start Free Trial</Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-blue-200 mb-4">
              <MapPin className="w-5 h-5" />
              <span>{data.name}, {data.state}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {data.tagline}
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl">
              {data.description}
            </p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-3">
                <div className="text-2xl font-bold">{data.localStats.shops}+</div>
                <div className="text-sm text-blue-200">Shops in {data.name}</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-3">
                <div className="text-2xl font-bold">{data.localStats.jobs}</div>
                <div className="text-sm text-blue-200">Jobs Managed</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg px-4 py-3">
                <div className="text-2xl font-bold">{data.localStats.satisfaction}</div>
                <div className="text-sm text-blue-200">Satisfaction Rate</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100" onClick={() => navigate("/signup")}>
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" onClick={() => navigate("/login")}>
                Login to Dashboard
              </Button>
            </div>
          </div>
        </section>

        {/* Areas Covered */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-center mb-6">
              Serving Repair Shops Across {data.name}
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {data.areas.map((area) => (
                <span key={area} className="bg-white px-4 py-2 rounded-full shadow-sm border text-gray-700">
                  <MapPin className="w-4 h-4 inline mr-1 text-blue-600" />
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-4">
              Why {data.name} Repair Shops Choose AfterSales.pro
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Built specifically for Indian repair businesses with local payment support, WhatsApp integration, and GST compliance.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, idx) => (
                <Card key={idx} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <feature.icon className="w-10 h-10 text-blue-600 mb-4" />
                    <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 bg-blue-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(data.testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-xl text-gray-800 mb-6">
                  "{data.testimonial.text}"
                </blockquote>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{data.testimonial.name}</div>
                    <div className="text-gray-600">{data.testimonial.shop}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Pricing CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Start Managing Your {data.name} Repair Shop Today
            </h2>
            <p className="text-gray-600 mb-8">
              Join {data.localStats.shops}+ repair shops in {data.name} already using AfterSales.pro
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6 text-center">
                  <h3 className="font-semibold mb-2">Free</h3>
                  <div className="text-3xl font-bold mb-2">₹0</div>
                  <div className="text-gray-500 mb-4">50 jobs/month</div>
                  <Button variant="outline" className="w-full" onClick={() => navigate("/signup")}>
                    Start Free
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-2 border-blue-500 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full">
                  Most Popular
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="font-semibold mb-2">Growth</h3>
                  <div className="text-3xl font-bold mb-2">₹999</div>
                  <div className="text-gray-500 mb-4">Unlimited jobs</div>
                  <Button className="w-full" onClick={() => navigate("/signup")}>
                    Start Trial
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-200">
                <CardContent className="p-6 text-center">
                  <h3 className="font-semibold mb-2">Enterprise</h3>
                  <div className="text-3xl font-bold mb-2">₹2,999</div>
                  <div className="text-gray-500 mb-4">Multi-branch</div>
                  <Button variant="outline" className="w-full" onClick={() => navigate("/signup")}>
                    Contact Sales
                  </Button>
                </CardContent>
              </Card>
            </div>

            <p className="text-sm text-gray-500">
              No credit card required • 14-day free trial on all paid plans • Cancel anytime
            </p>
          </div>
        </section>

        {/* FAQ Section for SEO */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">
              FAQs About Repair Shop Software in {data.name}
            </h2>
            
            <div className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">
                    What is the best repair shop software in {data.name}?
                  </h3>
                  <p className="text-gray-600">
                    AfterSales.pro is trusted by {data.localStats.shops}+ repair shops in {data.name}. It offers WhatsApp integration, GST invoicing, inventory management, and multi-branch support at affordable pricing starting from ₹0.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">
                    How much does repair shop software cost in {data.name}?
                  </h3>
                  <p className="text-gray-600">
                    AfterSales.pro offers a free plan with 50 jobs/month, perfect for small shops. Paid plans start at ₹499/month for unlimited jobs. Much more affordable than international alternatives like RepairDesk (₹4,000+/month).
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">
                    Does AfterSales.pro support shops in {data.areas.slice(0, 3).join(", ")}?
                  </h3>
                  <p className="text-gray-600">
                    Yes! AfterSales.pro supports repair shops across all areas of {data.name} including {data.areas.join(", ")}. Our cloud-based software works anywhere with internet access.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">
                    Can I manage multiple branches in {data.name} with AfterSales.pro?
                  </h3>
                  <p className="text-gray-600">
                    Absolutely! Our Enterprise plan supports unlimited branches. Many {data.name} repair chains use AfterSales.pro to manage inventory, staff, and jobs across all their locations from a single dashboard.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-16 bg-blue-700 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Grow Your {data.name} Repair Business?
            </h2>
            <p className="text-blue-100 mb-8">
              Join {data.repairShops} repair shops in {data.name}. Start your free trial today.
            </p>
            <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100" onClick={() => navigate("/signup")}>
              Start Free Trial - No Credit Card Required
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>

        {/* Simple Footer */}
        <footer className="bg-gray-900 text-gray-400 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p>© 2024 AfterSales.pro - Repair Shop Software for {data.name}</p>
            <div className="flex justify-center gap-4 mt-4 text-sm">
              <a href="/privacy-policy" className="hover:text-white">Privacy Policy</a>
              <a href="/terms-of-service" className="hover:text-white">Terms of Service</a>
              <a href="/contact" className="hover:text-white">Contact Us</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
